import { useEffect, useState } from "react";
import { Copy, Plus, Ticket } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const inYear = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const inputCls = "bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 text-sm text-white outline-none";

const CreateForm = ({ onCreated }) => {
  const [f, setF] = useState({ email: "", amount: "", expires_at: inYear(), note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vouchers", { ...f, amount: parseFloat(f.amount) });
      navigator.clipboard?.writeText(data.code);
      toast.success(`Buono ${data.code} creato e copiato`);
      setF({ email: "", amount: "", expires_at: inYear(), note: "" });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione buono");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-[#C9A227]/20 rounded-sm p-5 bg-[#0b0b0d] grid md:grid-cols-5 gap-3 items-end">
      <input data-testid="voucher-new-email" required type="email" placeholder="Email cliente" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${inputCls} md:col-span-2`} />
      <input data-testid="voucher-new-amount" required type="number" min="0.01" step="0.01" placeholder="Importo €" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} />
      <input data-testid="voucher-new-expiry" required type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className={inputCls} />
      <button data-testid="voucher-new-submit" type="submit" disabled={busy} className="btn-gold justify-center disabled:opacity-60"><Plus size={14} /> Crea buono</button>
      <input data-testid="voucher-new-note" placeholder="Nota interna (es. reso ordine #ABC123)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={`${inputCls} md:col-span-5`} />
    </form>
  );
};

const VoucherRow = ({ v, refresh }) => {
  const expired = v.expires_at < new Date().toISOString().slice(0, 10);
  const update = async (patch) => {
    try {
      await api.put(`/admin/vouchers/${v.voucher_id}`, patch);
      toast.success("Buono aggiornato");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const copy = () => { navigator.clipboard?.writeText(v.code); toast.success("Codice copiato"); };

  return (
    <tr data-testid={`voucher-row-${v.code}`} className="border-b border-[#C9A227]/8 hover:bg-[#C9A227]/5">
      <td className="px-3 py-3">
        <button onClick={copy} className="flex items-center gap-2 font-mono-ps text-[#C9A227] hover:text-white"><Copy size={12} />{v.code}</button>
        {v.note && <div className="text-[11px] text-[#6B7280] mt-1">{v.note}</div>}
      </td>
      <td className="px-3 py-3 text-white/85">{v.email}</td>
      <td className="px-3 py-3 font-mono-ps text-[#9CA3AF]">€ {v.initial_amount.toFixed(2)}</td>
      <td data-testid={`voucher-balance-${v.code}`} className="px-3 py-3 font-mono-ps text-white font-bold">€ {v.balance.toFixed(2)}</td>
      <td className="px-3 py-3">
        <input type="date" defaultValue={v.expires_at} data-testid={`voucher-expiry-${v.code}`} onBlur={(e) => e.target.value !== v.expires_at && update({ expires_at: e.target.value })} className={`${inputCls} py-1 ${expired ? "border-[#DC2626] text-[#DC2626]" : ""}`} />
      </td>
      <td className="px-3 py-3">
        <button data-testid={`voucher-toggle-${v.code}`} onClick={() => update({ active: !v.active })} className={`text-xs font-mono-ps uppercase tracking-widest ${v.active ? "text-emerald-400" : "text-[#DC2626]"}`}>
          {v.active ? "Attivo" : "Disattivato"}
        </button>
      </td>
    </tr>
  );
};

export const VouchersPanel = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/vouchers").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const outstanding = list.filter((v) => v.active).reduce((s, v) => s + v.balance, 0);

  return (
    <div className="mt-6 space-y-6">
      <div className="flex items-center gap-2 text-[#C9A227]">
        <Ticket size={16} />
        <span className="text-xs font-mono-ps uppercase tracking-widest">Buoni negozio — credito attivo totale € {outstanding.toFixed(2)}</span>
      </div>
      <CreateForm onCreated={load} />
      <div className="border border-[#C9A227]/15 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0b0b0d] border-b border-[#C9A227]/15">
            <tr className="text-left">
              {["Codice", "Cliente", "Valore", "Saldo", "Scadenza", "Stato"].map((h) => (
                <th key={h} className="px-3 py-3 font-mono-ps text-[10px] tracking-widest uppercase text-[#C9A227]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((v) => <VoucherRow key={v.voucher_id} v={v} refresh={load} />)}
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[#9CA3AF]">Nessun buono emesso.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
